import React, { useState } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import ColorPicker from '../UI/ColorPicker';
import './Scenarios.css';

const Scenarios = ({ scenarios }) => {
  const [customScenarios, setCustomScenarios] = useState([
    { name: 'Conservador', interestRate: 0.5, color: '#4682B4' },
    { name: 'Moderado', interestRate: 0.8, color: '#6A5ACD' },
    { name: 'Arrojado', interestRate: 1.2, color: '#FF7F50' },
  ]);
  
  const [formData, setFormData] = useState({
    initialCapital: 1000,
    monthlyContribution: 100,
    period: 60, // 5 anos
  });
  
  const [scenarioResults, setScenarioResults] = useState([]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0,
    });
  };
  
  const handleScenarioChange = (index, e) => {
    const { name, value } = e.target;
    const updatedScenarios = [...customScenarios];
    updatedScenarios[index] = {
      ...updatedScenarios[index],
      [name]: name === 'interestRate' ? parseFloat(value) || 0 : value,
    };
    setCustomScenarios(updatedScenarios);
  };
  
  const calculateScenarios = () => {
    const results = customScenarios.map(scenario => {
      const monthlyRate = scenario.interestRate / 100;
      let totalAmount = formData.initialCapital;
      let totalCapital = formData.initialCapital;
      
      for (let i = 0; i < formData.period; i++) {
        totalAmount = totalAmount * (1 + monthlyRate) + formData.monthlyContribution;
        totalCapital += formData.monthlyContribution;
      }
      
      const totalInterest = totalAmount - totalCapital;
      const returnRate = (totalInterest / totalCapital) * 100;
      
      return {
        name: scenario.name,
        totalAmount,
        totalCapital,
        totalInterest,
        returnRate,
        color: scenario.color,
      };
    });
    
    setScenarioResults(results);
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };
  
  return (
    <div className="scenarios-container">
      <h2>Comparação de Cenários</h2>
      
      <div className="scenarios-form">
        <div className="form-group">
          <label htmlFor="initialCapital">Capital Inicial (R$)</label>
          <input
            type="number"
            id="initialCapital"
            name="initialCapital"
            value={formData.initialCapital}
            onChange={handleInputChange}
            min="0"
          />
          <span className="input-highlight"></span>
        </div>
        
        <div className="form-group">
          <label htmlFor="monthlyContribution">Aporte Mensal (R$)</label>
          <input
            type="number"
            id="monthlyContribution"
            name="monthlyContribution"
            value={formData.monthlyContribution}
            onChange={handleInputChange}
            min="0"
          />
          <span className="input-highlight"></span>
        </div>
        
        <div className="form-group">
          <label htmlFor="period">Período (meses)</label>
          <input
            type="number"
            id="period"
            name="period"
            value={formData.period}
            onChange={handleInputChange}
            min="1"
          />
          <span className="input-highlight"></span>
          <div className="period-buttons">
            <button 
              type="button" 
              onClick={() => setFormData({...formData, period: 12})}
              className="period-preset-button"
            >
              1 ano
            </button>
            <button 
              type="button" 
              onClick={() => setFormData({...formData, period: 60})}
              className="period-preset-button"
            >
              5 anos
            </button>
            <button 
              type="button" 
              onClick={() => setFormData({...formData, period: 120})}
              className="period-preset-button"
            >
              10 anos
            </button>
          </div>
        </div>
      </div>
      
      <div className="custom-scenarios">
        <h3>Cenários Personalizados</h3>
        
        {customScenarios.map((scenario, index) => (
          <div key={index} className="scenario-item">
            <div className="form-group">
              <label htmlFor={`scenarioName${index}`}>Nome do Cenário</label>
              <input
                type="text"
                id={`scenarioName${index}`}
                name="name"
                value={scenario.name}
                onChange={(e) => handleScenarioChange(index, e)}
              />
              <span className="input-highlight"></span>
            </div>
            
            <div className="form-group">
              <label htmlFor={`scenarioRate${index}`}>Taxa de Juros (% ao mês)</label>
              <input
                type="number"
                id={`scenarioRate${index}`}
                name="interestRate"
                value={scenario.interestRate}
                onChange={(e) => handleScenarioChange(index, e)}
                step="0.01"
                min="0"
              />
              <span className="input-highlight"></span>
            </div>
            
            <div className="form-group">
              <label htmlFor={`scenarioColor${index}`}>Cor</label>
              <ColorPicker
                id={`scenarioColor${index}`}
                name="color"
                value={scenario.color}
                onChange={(e) => handleScenarioChange(index, e)}
              />
            </div>
          </div>
        ))}
        
        <button className="calculate-button" onClick={calculateScenarios}>
          Calcular Cenários
        </button>
      </div>
      
      {scenarioResults.length > 0 && (
        <div className="scenarios-results">
          <h3>Resultados Comparativos</h3>
          
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={scenarioResults} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={formatCurrency} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              {scenarioResults.map((scenario, index) => (
                <Bar 
                  key={index}
                  dataKey="totalAmount" 
                  name={`${scenario.name} - Total`} 
                  fill={scenario.color}
                  stackId={index}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          
          <div className="scenarios-summary">
            <table className="scenarios-table">
              <thead>
                <tr>
                  <th>Cenário</th>
                  <th>Valor Total</th>
                  <th>Capital Investido</th>
                  <th>Juros Acumulados</th>
                  <th>Rentabilidade</th>
                </tr>
              </thead>
              <tbody>
                {scenarioResults.map((result, index) => (
                  <tr key={index} style={{ borderLeft: `4px solid ${result.color}` }}>
                    <td>
                      <div className="scenario-label">
                        <span className="color-dot" style={{ backgroundColor: result.color }}></span>
                        {result.name}
                      </div>
                    </td>
                    <td>{formatCurrency(result.totalAmount)}</td>
                    <td>{formatCurrency(result.totalCapital)}</td>
                    <td>{formatCurrency(result.totalInterest)}</td>
                    <td>{result.returnRate.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scenarios;